import React, { useState } from 'react';
import { Button, Modal, InputNumber, Select, message, Space, Typography } from 'antd';
import { DollarOutlined, WalletOutlined } from '@ant-design/icons';
import { useWallet } from '@solana/wallet-adapter-react';
import { Connection, PublicKey, Transaction, SystemProgram, LAMPORTS_PER_SOL } from '@solana/web3.js';

// Import SPL Token functions - using dynamic import as fallback
let splToken;
try {
  splToken = require('@solana/spl-token');
} catch (e) {
  console.warn('Failed to load @solana/spl-token, will use dynamic import');
}

const { Text } = Typography;

// SCAI 官方地址
const SCAI_OFFICIAL_ADDRESS = 'CoQze6FGuz11sbxztVbaXcwt5GBAC5aUidHDT54VzrFL';

// Token Mint 地址
const USDT_MINT = 'Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB'; // USDT (Tether USD)
const SCAI_MINT = '6g3FkkCcDXh6PGUSVAXN1AKbpyzQtygj7SATK41bonk'; // SCAI Token

const TipButton = ({ uploader, paperTitle }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [amount, setAmount] = useState(0.01); // 🆕 默认金额改为 0.01 SOL
  const [currency, setCurrency] = useState('SOL');
  const [loading, setLoading] = useState(false);
  const { publicKey, sendTransaction, connected } = useWallet();

  // 🆕 根据不同代币设置推荐金额
  const getRecommendedAmounts = () => {
    switch (currency) {
      case 'SOL':
        return [0.01, 0.05, 0.1, 0.5, 1];
      case 'USDT':
        return [1, 5, 10, 20, 50];
      case 'SCAI':
        return [1000, 5000, 10000, 50000, 100000];
      default:
        return [0.1, 0.5, 1, 5, 10];
    }
  };

  // 🆕 当切换代币时，自动设置推荐金额
  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
    const recommendedAmounts = getRecommendedAmounts();
    // 根据新代币类型设置默认金额
    if (newCurrency === 'SOL') {
      setAmount(0.01);
    } else if (newCurrency === 'USDT') {
      setAmount(1);
    } else if (newCurrency === 'SCAI') {
      setAmount(1000);
    }
  };

  // 确定接收地址
  const recipientAddress = uploader === 'scai' ? SCAI_OFFICIAL_ADDRESS : uploader;

  const handleTip = async () => {
    if (!connected || !publicKey) {
      message.error('Please connect your wallet first');
      return;
    }

    if (!amount || amount <= 0) {
      message.error('Please enter a valid amount');
      return;
    }

    // Load SPL Token dynamically if not already loaded
    if (!splToken) {
      try {
        splToken = await import('@solana/spl-token');
      } catch (e) {
        message.error('Failed to load SPL Token library. Please refresh the page.');
        console.error('Failed to load @solana/spl-token:', e);
        return;
      }
    }

    setLoading(true);

    try {
      // 使用 mainnet-beta 连接
      const connection = new Connection('https://go.getblock.io/1ef14a8a5ed84f7bb94e66b6f5617e02', 'confirmed');
      const recipientPubkey = new PublicKey(recipientAddress);

      const transaction = new Transaction();

      if (currency === 'SOL') {
        // SOL 原生转账
        console.log(`Transferring ${amount} SOL to ${recipientAddress}`);
        const lamports = Math.floor(amount * LAMPORTS_PER_SOL);

        transaction.add(
          SystemProgram.transfer({
            fromPubkey: publicKey,
            toPubkey: recipientPubkey,
            lamports: lamports,
          })
        );
      } else {
        // SPL Token 转账 (USDT 或 SCAI)
        const mintAddress = currency === 'USDT' ? USDT_MINT : SCAI_MINT;
        const mintPubkey = new PublicKey(mintAddress);

        console.log(`Transferring ${amount} ${currency} (mint: ${mintAddress}) to ${recipientAddress}`);

        // 获取 mint 信息以获取正确的 decimals
        const mintInfo = await splToken.getMint(connection, mintPubkey);
        const decimals = mintInfo.decimals;
        console.log(`Token decimals: ${decimals}`);

        // 计算实际转账金额（考虑 decimals）
        const transferAmount = Math.floor(amount * Math.pow(10, decimals));
        console.log(`Transfer amount (with decimals): ${transferAmount}`);

        // 获取或创建发送者的关联代币账户
        const fromTokenAccount = await splToken.getOrCreateAssociatedTokenAccount(
          connection,
          publicKey, // payer (这里会用 wallet adapter 签名)
          mintPubkey,
          publicKey, // owner
          false, // allowOwnerOffCurve
          'confirmed',
          {},
          undefined,
          { skipPreflight: false }
        );

        console.log(`From token account: ${fromTokenAccount.address.toString()}`);

        // 获取或创建接收者的关联代币账户
        const toTokenAccount = await splToken.getOrCreateAssociatedTokenAccount(
          connection,
          publicKey, // payer (发送者支付创建账户的费用)
          mintPubkey,
          recipientPubkey, // owner (接收者)
          false,
          'confirmed',
          {},
          undefined,
          { skipPreflight: false }
        );

        console.log(`To token account: ${toTokenAccount.address.toString()}`);

        // 创建转账指令
        transaction.add(
          splToken.createTransferInstruction(
            fromTokenAccount.address, // source
            toTokenAccount.address,   // destination
            publicKey,                // owner
            transferAmount            // amount
          )
        );
      }

      // 获取最新的 blockhash
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash('confirmed');
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = publicKey;

      console.log('Sending transaction...');

      // 使用 wallet adapter 发送交易
      const signature = await sendTransaction(transaction, connection);

      console.log(`Transaction sent: ${signature}`);
      console.log('Waiting for confirmation...');

      // 等待确认
      await connection.confirmTransaction({
        signature,
        blockhash,
        lastValidBlockHeight
      }, 'confirmed');

      console.log('Transaction confirmed!');

      message.success(
        <span>
          Successfully tipped {amount} {currency}!<br />
          <a
            href={`https://solscan.io/tx/${signature}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1890ff' }}
          >
            View on Solscan
          </a>
        </span>,
        5
      );

      setModalVisible(false);
      setAmount(0.1);
    } catch (error) {
      console.error('Tip transaction failed:', error);

      let errorMessage = 'Tip failed';
      if (error.message) {
        errorMessage += `: ${error.message}`;
      }

      // 处理常见错误
      if (error.message?.includes('insufficient funds')) {
        errorMessage = 'Insufficient funds in your wallet';
      } else if (error.message?.includes('User rejected')) {
        errorMessage = 'Transaction cancelled by user';
      }

      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    if (!connected) {
      message.warning('Please connect your wallet to tip');
      return;
    }
    setModalVisible(true);
  };

  return (
    <>
      <Button
        icon={<DollarOutlined />}
        onClick={handleOpenModal}
        style={{
          borderColor: '#52c41a',
          color: '#52c41a',
          background: 'transparent',
        }}
      >
        Tip
      </Button>

      <Modal
        title={
          <Space>
            <WalletOutlined style={{ color: '#52c41a' }} />
            <span>Tip the Uploader</span>
          </Space>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={400}
      >
        <div style={{ padding: '20px 0' }}>
          <div style={{ marginBottom: '16px' }}>
            <Text strong>Paper: </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {paperTitle?.length > 50 ? paperTitle.slice(0, 50) + '...' : paperTitle}
            </Text>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <Text strong>Uploader: </Text>
            <Text code style={{ fontSize: '12px' }}>
              {uploader === 'scai' ? 'SCAI Official' : `${recipientAddress.slice(0, 8)}...${recipientAddress.slice(-8)}`}
            </Text>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <Text strong style={{ display: 'block', marginBottom: '8px' }}>Select Currency:</Text>
            <Select
              value={currency}
              onChange={handleCurrencyChange}
              style={{ width: '100%', marginBottom: '12px' }}
              size="large"
            >
              <Select.Option value="SOL">
                <Space align="center">
                  <img
                    src="/sollogo.png"
                    alt="SOL"
                    style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'contain' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <span style={{ fontWeight: 500 }}>SOL</span>
                  <span style={{ fontSize: '12px', color: '#8c8c8c' }}>(Recommended: 0.01)</span>
                </Space>
              </Select.Option>
              <Select.Option value="USDT">
                <Space align="center">
                  <div style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: '#26A17B',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    fontWeight: 'bold',
                    color: 'white'
                  }}>
                    ₮
                  </div>
                  <span style={{ fontWeight: 500 }}>USDT</span>
                  <span style={{ fontSize: '12px', color: '#8c8c8c' }}>(Recommended: 1)</span>
                </Space>
              </Select.Option>
              <Select.Option value="SCAI">
                <Space align="center">
                  <img
                    src="/logo512.png"
                    alt="SCAI"
                    style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'contain' }}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <span style={{ fontWeight: 500 }}>SCAI</span>
                  <span style={{ fontSize: '12px', color: '#8c8c8c' }}>(Recommended: 1000)</span>
                </Space>
              </Select.Option>
            </Select>

            <Text strong style={{ display: 'block', marginBottom: '8px' }}>
              Amount:
              <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#8c8c8c', marginLeft: '8px' }}>
                {currency === 'SOL' && '(Recommended: 0.01 SOL)'}
                {currency === 'USDT' && '(Recommended: 1 USDT)'}
                {currency === 'SCAI' && '(Recommended: 1000 SCAI)'}
              </span>
            </Text>
            <InputNumber
              min={currency === 'SCAI' ? 1 : 0.001}
              max={currency === 'SCAI' ? 1000000 : 10000}
              step={currency === 'SCAI' ? 100 : (currency === 'SOL' ? 0.01 : 1)}
              value={amount}
              onChange={setAmount}
              style={{ width: '100%', marginBottom: '12px' }}
              size="large"
              placeholder={`Enter ${currency} amount`}
            />

            {/* 🆕 根据代币类型显示不同的预设金额按钮 */}
            <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              {getRecommendedAmounts().map((amt) => (
                <Button
                  key={amt}
                  size="small"
                  onClick={() => setAmount(amt)}
                  type={amount === amt ? 'primary' : 'default'}
                  style={{
                    minWidth: '60px',
                    marginBottom: '4px'
                  }}
                >
                  {amt >= 1000 ? `${(amt / 1000).toFixed(0)}K` : amt}
                </Button>
              ))}
            </Space>
          </div>

          <div style={{
            marginBottom: '24px',
            padding: '16px',
            background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
            borderRadius: '8px',
            border: '1px solid #bae6fd'
          }}>
            <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }}>💡</span>
              <Text strong style={{ fontSize: '14px', color: '#0369a1' }}>
                Support the Uploader
              </Text>
            </div>
            <div style={{ paddingLeft: '28px' }}>
              <Text type="secondary" style={{ fontSize: '12px', color: '#0284c7', display: 'block', marginBottom: '4px' }}>
                ✓ Tips go directly to the uploader's wallet
              </Text>
              <Text type="secondary" style={{ fontSize: '12px', color: '#0284c7', display: 'block', marginBottom: '4px' }}>
                ✓ Choose from SOL, USDT, or SCAI tokens
              </Text>
              <Text type="secondary" style={{ fontSize: '12px', color: '#0284c7', display: 'block' }}>
                ✓ Network fees paid by sender
              </Text>
            </div>
          </div>

          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setModalVisible(false)}>
              Cancel
            </Button>
            <Button
              type="primary"
              loading={loading}
              onClick={handleTip}
              style={{
                backgroundColor: '#52c41a',
                borderColor: '#52c41a',
              }}
            >
              Send Tip
            </Button>
          </Space>
        </div>
      </Modal>
    </>
  );
};

export default TipButton;

